import {createSlice, PayloadAction, AnyAction} from '@reduxjs/toolkit';
import {IAuthState, IModalParams, IViewParams} from 'modules/auth/types';
import {HYDRATE} from 'next-redux-wrapper';
import {IRootReducer} from 'core/redux/reducers';

export const authModuleName = 'auth';

const initialState: IAuthState = {
    authUserData: {},
    confirmationSource: {},
    emailAndPasswordData: {
        email: '',
        password: '',
    },
    isAuthScenarioTotallyComplete: null,
    modalParams: {},
    recoveryPasswordData: {
        email: '',
    },
    registrationData: {
        phone: '',
        birthdayDate: '',
        code: '',
    },
    token: '',
    userFormData: null,
    viewParams: {},
};

export const authSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(HYDRATE, (state, action: AnyAction) => {
            const payload = action.payload as IRootReducer | undefined;
            if (!payload) {
                return state;
            }

            return payload[authModuleName];
        });
    },
    initialState,
    name: authModuleName,
    reducers: {
        actionSetEmail(state, {payload}: PayloadAction<IAuthState['registrationData']['email']>) {
            state.registrationData.email = payload;
        },

        actionSetAuthenticated(state, {payload}: PayloadAction<IAuthState['authUserData']['isAuthenticated']>) {
            state.authUserData.isAuthenticated = payload;
        },

        actionSetConfirmationSource(state, {payload}: PayloadAction<IAuthState['confirmationSource']>) {
            state.confirmationSource = payload;
        },

        actionSetEmailAndPasswordData(state, {payload}: PayloadAction<IAuthState['emailAndPasswordData']>) {
            state.emailAndPasswordData = payload;
        },

        actionSetEmailIsChecked(state, {payload}: PayloadAction<IAuthState['authUserData']['isEmailChecked']>) {
            state.authUserData.isEmailChecked = payload;
        },

        actionSetEmailIsConfirmed(state, {payload}: PayloadAction<IAuthState['authUserData']['isEmailConfirmed']>) {
            state.authUserData.isEmailConfirmed = payload;
        },

        actionSetIsAuthScenarioTotallyComplete(
            state,
            {payload}: PayloadAction<IAuthState['isAuthScenarioTotallyComplete']>,
        ) {
            state.isAuthScenarioTotallyComplete = payload;
        },

        actionSetModalParams(state, {payload}: PayloadAction<IModalParams>) {
            state.modalParams[payload.name] = payload;
        },

        actionSetPhoneIsChecked(state, {payload}: PayloadAction<IAuthState['authUserData']['isPhoneChecked']>) {
            state.authUserData.isPhoneChecked = payload;
        },

        actionSetPhoneIsConfirmed(state, {payload}: PayloadAction<IAuthState['authUserData']['isPhoneConfirmed']>) {
            state.authUserData.isPhoneConfirmed = payload;
        },

        actionSetPrivateToken(state, {payload}: PayloadAction<IAuthState['token']>) {
            state.token = payload;
        },

        actionSetRecoveryPasswordData(state, {payload}: PayloadAction<IAuthState['recoveryPasswordData']>) {
            state.recoveryPasswordData = payload;
        },

        actionSetRegistrationData(state, {payload}: PayloadAction<IAuthState['registrationData']>) {
            state.registrationData = {...state.registrationData, ...payload};
        },

        actionSetSMSCode(state, {payload}: PayloadAction<IAuthState['registrationData']['code']>) {
            state.registrationData.code = payload;
        },

        actionSetBirthdayDateData(state, {payload}: PayloadAction<IAuthState['registrationData']['birthdayDate']>) {
            state.registrationData.birthdayDate = payload;
        },

        actionSetRepeatCodeDuration(
            state,
            {payload}: PayloadAction<IAuthState['authUserData']['repeatAfterCodeDuration']>,
        ) {
            state.authUserData = {
                ...state.authUserData,
                repeatAfterCodeDuration: payload,
            };
        },

        actionSetViewParams(state, {payload}: PayloadAction<IViewParams>) {
            state.viewParams[payload.name] = payload;
        },
    },
});

export const {
    actions: {
        actionSetConfirmationSource,
        actionSetModalParams,
        actionSetPrivateToken,
        actionSetViewParams,
        actionSetAuthenticated,
        actionSetPhoneIsConfirmed,
        actionSetEmailIsChecked,
        actionSetRepeatCodeDuration,
        actionSetEmailIsConfirmed,
        actionSetPhoneIsChecked,
        actionSetIsAuthScenarioTotallyComplete,
        actionSetRegistrationData,
        actionSetRecoveryPasswordData,
        actionSetSMSCode,
        actionSetBirthdayDateData,
        actionSetEmailAndPasswordData,
        actionSetEmail,
    },
} = authSlice;

export const {reducer: authReducer} = authSlice;
